import { useState, useCallback } from 'react'

export interface Worker {
  name: string
  persona: string
  status: string
  created_at?: string
  tasks_completed?: number
  tasks_failed?: number
}

export interface HeartbeatSample {
  timestamp: string
  active: boolean
}

export interface ActivityLogEntry {
  timestamp: string
  message: string
}

export interface WorkerDetails {
  worker_name: string
  heartbeats: HeartbeatSample[]
  activity_log: ActivityLogEntry[]
}

interface UseWorkersOptions {
  onMessage?: (data: any) => void
  send?: (data: any) => void
}

export function useWorkers(options: UseWorkersOptions = {}) {
  const [workers, setWorkers] = useState<Worker[]>([])
  const [workerDetails, setWorkerDetails] = useState<WorkerDetails | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  const requestWorkerDetails = useCallback((workerName: string) => {
    if (options.send) {
      setIsLoadingDetails(true)
      setWorkerDetails(null)
      options.send({ type: 'worker:request-details', worker_name: workerName })
    }
  }, [options.send])

  const clearWorkerDetails = useCallback(() => {
    setWorkerDetails(null)
    setIsLoadingDetails(false)
  }, [])

  const handleWebSocketMessage = useCallback((data: any) => {
    // Call custom handler if provided
    options.onMessage?.(data)

    // Handle worker:details response
    if (data.type === 'worker:details') {
      setWorkerDetails({
        worker_name: data.worker_name,
        heartbeats: data.heartbeats || [],
        activity_log: (data.activity_log || []).map((log: any) => ({
          timestamp: log.timestamp,
          message: log.message,
        })),
      })
      setIsLoadingDetails(false)
      return
    }

    // Handle worker-related messages
    if (data.type === 'worker:spawn') {
      setWorkers((prev) => {
        const worker = data.worker as Worker
        // Don't add if already exists
        if (prev.some((w) => w.name === worker.name)) {
          return prev
        }
        return [...prev, worker]
      })
    } else if (data.type === 'worker:update') {
      setWorkers((prev) => {
        const worker = data.worker as Worker
        const index = prev.findIndex((w) => w.name === worker.name)
        if (index >= 0) {
          const updated = [...prev]
          updated[index] = { ...updated[index], ...worker }
          return updated
        }
        return prev
      })
    } else if (data.type === 'worker:died') {
      setWorkers((prev) => {
        const index = prev.findIndex((w) => w.name === data.name)
        if (index >= 0) {
          const updated = [...prev]
          const existing = updated[index]
          if (existing) {
            updated[index] = {
              name: existing.name,
              persona: existing.persona,
              status: 'inactive',
              created_at: existing.created_at,
              tasks_completed: existing.tasks_completed,
              tasks_failed: existing.tasks_failed,
            }
          }
          return updated
        }
        return prev
      })
    } else if (data.type === 'workers:list') {
      // Initial load or bulk update
      setWorkers(data.workers || [])
    }
  }, [options])

  return {
    workers,
    workerDetails,
    isLoadingDetails,
    requestWorkerDetails,
    clearWorkerDetails,
    handleWebSocketMessage,
  }
}
